import React from 'react'
import {
  Box,
  Paper,
  Typography,
  TextField,
  Grid,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material'
import { Controller, Control, FormState } from 'react-hook-form'

interface EquipmentStandardsTabProps {
  control: Control<any>
  formState: FormState<any>
  watch: (name: string) => any
  validateAHUNumber: (value: string) => void
  OUTDOOR_CONDITIONS: any[]
  selectedOutdoorCondition: string
  getCurrentOutdoorCondition: (code: string) => any
  coolingCoilType: string
  preheatingCoilType: string
  heatingCoilType: string
  reheatingCoilType: string
  commonCoil: boolean
  humidifierType: string
  coilValidationError: string
}

const EquipmentStandardsTab: React.FC<EquipmentStandardsTabProps> = ({
  control,
  formState,
  watch,
  validateAHUNumber,
  OUTDOOR_CONDITIONS,
  selectedOutdoorCondition,
  getCurrentOutdoorCondition,
  coolingCoilType,
  preheatingCoilType,
  heatingCoilType,
  reheatingCoilType,
  commonCoil,
  humidifierType,
  coilValidationError
}) => {
  return (
    
    <Grid container spacing={3} >
      {/* 1. 장비 개요 (Equipment Overview) - 상단 */}
      <Grid item xs={12}>
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom sx={{
            bgcolor: 'grey.200', fontWeight: 600,
            p: 1,
            mb: 2,
            borderRadius: 1
          }}>
            1. 장비 개요
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>AHU No.</Typography>
              <Controller
                name="ahuNo"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    fullWidth
                    placeholder="AH-001"
                    inputProps={{ 'aria-label': 'AHU 번호' }}
                    onBlur={(e) => {
                      field.onBlur(e)
                      validateAHUNumber(e.target.value)
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>계통 명칭</Typography>
              <Controller
                name="systemName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    fullWidth
                    placeholder="B1F 상가"
                    inputProps={{ 'aria-label': '계통 명칭' }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1.5}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>수량</Typography>
              <Controller
                name="quantity"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    fullWidth
                    type="number"
                    inputProps={{ 'aria-label': '수량', min: 1 }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1.5}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>설치 위치</Typography>
              <Controller
                name="location"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    fullWidth
                    placeholder="B3F 기계실"
                    inputProps={{ 'aria-label': '설치 위치' }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* 2. 기본 Data (Basic Data) */}
      <Grid item xs={12}>
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom sx={{
            bgcolor: 'grey.200', fontWeight: 600,
            p: 1,
            mb: 2,
            borderRadius: 1
          }}>
            2. 기본 Data
          </Typography>
          <Grid container spacing={3}>
            {/* 외기 설계 조건 및 적용시각 */}
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>외기설계</Typography>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                {/* 설계조건 선택 */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Controller
                    name="outdoorDesignCondition"
                    control={control}
                    render={({ field }) => (
                      <FormControl size="small" sx={{ minWidth: 80 }}>
                        <Select {...field} aria-label="외기설계 조건">
                          {OUTDOOR_CONDITIONS.map(condition => (
                            <MenuItem key={condition.code} value={condition.code}>
                              {condition.code}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                  {/* 설계조건 정보 표시 */}
                  {(() => {
                    const selectedCondition = getCurrentOutdoorCondition(selectedOutdoorCondition)
                    return selectedCondition ? (
                      <Typography variant="caption" sx={{
                        fontSize: '0.75rem',
                        color: 'text.secondary',
                        lineHeight: 1.2,
                        maxWidth: 250
                      }}>
                        {selectedCondition.code}: 여름철 {selectedCondition.summer.temperature}℃, {selectedCondition.summer.humidity}% |
                        겨울철 {selectedCondition.winter.temperature}℃, {selectedCondition.winter.humidity}%
                      </Typography>
                    ) : null
                  })()}
                </Box>

                {/* 외기 적용시각 */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 400 }}>
                  {/* 라벨 */}
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 'medium', minWidth: 80 }}>
                    외기 적용시각
                  </Typography>

                  {/* 시간 입력 필드 */}
                  <Controller
                    name="outdoorApplicationTime"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        size="small"
                        type="number"
                        disabled={watch('outdoorTimeApplication') === 'standard'}
                        placeholder={watch('outdoorTimeApplication') === 'standard' ? '15' : '시간'}
                        InputProps={{
                          endAdornment: <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>시</Typography>
                        }}
                        inputProps={{ min: 0, max: 23, step: 1 }}
                        sx={{
                          width: 80,
                          '& .MuiInputBase-input:disabled': {
                            color: 'text.secondary',
                            webkitTextFillColor: 'rgba(0, 0, 0, 0.6)'
                          }
                        }}
                        value={watch('outdoorTimeApplication') === 'standard' ? '15' : field.value}
                      />
                    )}
                  />

                  {/* 적용 방식 선택 */}
                  <Controller
                    name="outdoorTimeApplication"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup
                        {...field}
                        row
                        sx={{ ml: 1 }}
                      >
                        <FormControlLabel
                          value="standard"
                          control={<Radio size="small" />}
                          label={
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                              표준 적용
                            </Typography>
                          }
                          sx={{ mr: 2 }}
                        />
                        <FormControlLabel
                          value="peak"
                          control={<Radio size="small" />}
                          label={
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                              임의 지정
                            </Typography>
                          }
                        />
                      </RadioGroup>
                    )}
                  />
                </Box>
              </Box>
            </Grid>

            {/* System은 내부적으로 'CAV' 고정값 사용 (화면에 표시 안 함) */}
            <Controller
              name="systemType"
              control={control}
              defaultValue="CAV"
              render={({ field }) => (
                <input type="hidden" {...field} value="CAV" />
              )}
            />

            {/* 배열 회수기, 회수 효율 */}
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>배열 회수기</Typography>
              <Controller
                name="heatRecoveryType"
                control={control}
                render={({ field }) => (
                  <FormControl size="small" fullWidth>
                    <Select {...field} aria-label="배열 회수기 타입">
                      <MenuItem value="None">None</MenuItem>
                      <MenuItem value="S.H">S.H (현열)</MenuItem>
                      <MenuItem value="T.H">T.H (전열)</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            {/* 배열 회수기 효율 입력 (S.H 또는 T.H 선택 시에만 표시) */}
            {watch('heatRecoveryType') !== 'None' && (
              <>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>냉방효율 (%)</Typography>
                  <Controller
                    name="coolingRecoveryEfficiency"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        size="small"
                        type="number"
                        fullWidth
                        placeholder="70"
                        inputProps={{ 'aria-label': '냉방용 회수 효율', min: 0, max: 100 }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>난방효율 (%)</Typography>
                  <Controller
                    name="heatingRecoveryEfficiency"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        size="small"
                        type="number"
                        fullWidth
                        placeholder="70"
                        inputProps={{ 'aria-label': '난방용 회수 효율', min: 0, max: 100 }}
                      />
                    )}
                  />
                </Grid>
              </>
            )}

            {/* 적용 여부 옵션 */}
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>적용 옵션</Typography>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <Controller
                  name="applyOutdoorCooling"
                  control={control}
                  defaultValue={true}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label="외기냉방 적용 여부"
                    />
                  )}
                />
              </Box>
            </Grid>

          </Grid>

          {/* 배열회수기 효율 정보 */}
          <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1, borderLeft: '4px solid #2196f3' }}>
            <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
              💡 배열회수기 효율 : 급배기량 비율이 감안된 장비 효율 입력
            </Typography>
          </Box>
        </Paper>
      </Grid>

      {/* 2. 코일 선정 기준 (Coil Selection Criteria) */}
      <Grid item xs={12}>
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom sx={{
            bgcolor: 'grey.200', fontWeight: 600,
            p: 1,
            mb: 2,
            borderRadius: 1
          }}>
            2. 코일 선정 기준
          </Typography>

          {/* 코일 공통 사용 체크박스 */}
          <Box sx={{ mb: 2 }}>
            <Controller
              name="commonCoil"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value || false} />}
                  label="냉/온수 겸용 코일 적용"
                />
              )}
            />
          </Box>

          <TableContainer>
            <Table size="small" aria-label="코일 선정 기준 테이블">
              <TableHead>
                <TableRow>
                  <TableCell rowSpan={2} sx={{ fontWeight: 'bold', verticalAlign: 'middle' }}>구분</TableCell>
                  <TableCell rowSpan={2} sx={{ fontWeight: 'bold', verticalAlign: 'middle' }}>Type</TableCell>
                  <TableCell colSpan={2} sx={{ fontWeight: 'bold', textAlign: 'center' }}>입출구 수온(℃)</TableCell>
                  <TableCell rowSpan={2} sx={{ fontWeight: 'bold', verticalAlign: 'middle' }}>열용량 (kJ/kg)</TableCell>
                  <TableCell rowSpan={2} sx={{ fontWeight: 'bold', verticalAlign: 'middle' }}>안전율</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', fontSize: '0.875rem' }}>입구</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', fontSize: '0.875rem' }}>출구</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* 냉방용 코일 그룹 헤더 */}
                <TableRow>
                  <TableCell colSpan={6} sx={{ bgcolor: '#E3F2FD', fontWeight: 'bold', py: 0.5 }}>
                    <Typography variant="body2" sx={{ color: '#1976D2' }}>냉방용 코일</Typography>
                  </TableCell>
                </TableRow>

                {/* 냉각 코일 (DDC) */}
                <TableRow>
                  <TableCell sx={{ color: '#1976D2', fontWeight: 'medium' }}>냉방</TableCell>
                  <TableCell>
                    <Controller
                      name="coolingCoilType"
                      control={control}
                      render={({ field }) => (
                        <FormControl size="small" fullWidth>
                          <Select {...field}>
                            <MenuItem value="None">None</MenuItem>
                            <MenuItem value="Water">Water</MenuItem>
                            <MenuItem value="DX">DX</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', pr: 1 }}>
                    {coolingCoilType === 'None' || coolingCoilType === 'DX' ? (
                      <Typography variant="body2" color="textSecondary">-</Typography>
                    ) : (
                      <Controller
                        name="coolingInletTemp"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} size="small" sx={{ width: 60 }} inputProps={{ style: { textAlign: 'right' } }} />
                        )}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ position: 'relative', pl: 0.5 }}>
                    {coolingCoilType === 'None' || coolingCoilType === 'DX' ? (
                      <Typography variant="body2" color="textSecondary">-</Typography>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" color="textSecondary" sx={{ minWidth: '10px' }}>/</Typography>
                        <Controller
                          name="coolingOutletTemp"
                          control={control}
                          render={({ field }) => (
                            <TextField {...field} size="small" sx={{ width: 60 }} />
                          )}
                        />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">-</Typography>
                  </TableCell>
                  <TableCell>
                    {coolingCoilType === 'None' ? (
                      <Typography variant="body2" color="textSecondary">-</Typography>
                    ) : (
                      <Controller
                        name="coolingSafetyFactor"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} size="small" sx={{ width: 60 }} />
                        )}
                      />
                    )}
                  </TableCell>
                </TableRow>

                 {/* 재열 코일 (DDRR) */}
                <TableRow>
                  <TableCell sx={{ color: '#1976D2', fontWeight: 'medium' }}>재열</TableCell>
                  <TableCell>
                    <Controller
                      name="reheatingCoilType"
                      control={control}
                      defaultValue="None"
                      render={({ field }) => (
                        <FormControl size="small" fullWidth>
                          <Select {...field}>
                            <MenuItem value="None">None</MenuItem>
                            <MenuItem value="Water">Water</MenuItem>
                            <MenuItem value="Steam">Steam</MenuItem>
                            <MenuItem value="Elec">Elec</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', pr: 1 }}>
                    {reheatingCoilType === 'None' || reheatingCoilType === 'Steam' || reheatingCoilType === 'Elec' ? (
                      <Typography variant="body2" color="textSecondary">-</Typography>
                    ) : (
                      <Controller
                        name="reheatingInletTemp"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} size="small" sx={{ width: 60 }} inputProps={{ style: { textAlign: 'right' } }} />
                        )}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ position: 'relative', pl: 0.5 }}>
                    {reheatingCoilType === 'None' || reheatingCoilType === 'Steam' || reheatingCoilType === 'Elec' ? (
                      <Typography variant="body2" color="textSecondary">-</Typography>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" color="textSecondary" sx={{ minWidth: '10px' }}>/</Typography>
                        <Controller
                          name="reheatingOutletTemp"
                          control={control}
                          render={({ field }) => (
                            <TextField {...field} size="small" sx={{ width: 60 }} />
                          )}
                        />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {reheatingCoilType === 'Steam' ? (
                      <Controller
                        name="reheatingHeatCapacity"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} size="small" sx={{ width: 80 }} placeholder="2454" />
                        )}
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {reheatingCoilType === 'None' ? (
                      <Typography variant="body2" color="textSecondary">-</Typography>
                    ) : (
                      <Controller
                        name="reheatingSafetyFactor"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} size="small" sx={{ width: 60 }} />
                        )}
                      />
                    )}
                  </TableCell>
                </TableRow>

                {/* 난방용 코일 그룹 헤더 */}
                <TableRow>
                  <TableCell colSpan={6} sx={{ bgcolor: '#FFEBEE', fontWeight: 'bold', py: 0.5 }}>
                    <Typography variant="body2" sx={{ color: '#D32F2F' }}>난방용 코일</Typography>
                  </TableCell>
                </TableRow>

                {/* 예열 코일 (DDRP) */}
                {!commonCoil && (
                  <TableRow>
                    <TableCell sx={{ color: '#D32F2F', fontWeight: 'medium' }}>예열</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Controller
                          name="preheatingCoilType"
                          control={control}
                          render={({ field }) => (
                            <FormControl size="small" fullWidth>
                              <Select {...field}>
                                <MenuItem value="None">None</MenuItem>
                                <MenuItem value="Water">Water</MenuItem>
                                <MenuItem value="Steam">Steam</MenuItem>
                                <MenuItem value="Elec">Elec</MenuItem>
                              </Select>
                            </FormControl>
                          )}
                        />
                        {preheatingCoilType !== 'None' && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', minWidth: 70 }}>
                              예열 후 온도
                            </Typography>
                            <Controller
                              name="phPreheatingTemp"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  size="small"
                                  sx={{ width: 70 }}
                                  inputProps={{ style: { textAlign: 'right' } }}
                                  placeholder="℃"
                                />
                              )}
                            />
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right', pr: 1 }}>
                      {preheatingCoilType === 'None' || preheatingCoilType === 'Steam' || preheatingCoilType === 'Elec' ? (
                        <Typography variant="body2" color="textSecondary">-</Typography>
                      ) : (
                        <Controller
                          name="preheatingInletTemp"
                          control={control}
                          render={({ field }) => (
                            <TextField {...field} size="small" sx={{ width: 60 }} inputProps={{ style: { textAlign: 'right' } }} />
                          )}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ position: 'relative', pl: 0.5 }}>
                      {preheatingCoilType === 'None' || preheatingCoilType === 'Steam' || preheatingCoilType === 'Elec' ? (
                        <Typography variant="body2" color="textSecondary">-</Typography>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" color="textSecondary" sx={{ minWidth: '10px' }}>/</Typography>
                          <Controller
                            name="preheatingOutletTemp"
                            control={control}
                            render={({ field }) => (
                              <TextField {...field} size="small" sx={{ width: 60 }} />
                            )}
                          />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {preheatingCoilType === 'Steam' ? (
                        <Controller
                          name="preheatingHeatCapacity"
                          control={control}
                          render={({ field }) => (
                            <TextField {...field} size="small" sx={{ width: 80 }} placeholder="2454" />
                          )}
                        />
                      ) : (
                        <Typography variant="body2" color="textSecondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {preheatingCoilType === 'None' ? (
                        <Typography variant="body2" color="textSecondary">-</Typography>
                      ) : (
                        <Controller
                          name="preheatingSafetyFactor"
                          control={control}
                          render={({ field }) => (
                            <TextField {...field} size="small" sx={{ width: 60 }} />
                          )}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                )}

                {/* 가열 코일 (DDRH) */}
                <TableRow>
                  <TableCell sx={{ color: '#D32F2F', fontWeight: 'medium' }}>가열</TableCell>
                  <TableCell>
                    <Controller
                      name="heatingCoilType"
                      control={control}
                      render={({ field }) => (
                        <FormControl size="small" fullWidth>
                          <Select {...field}>
                            <MenuItem value="None">None</MenuItem>
                            <MenuItem value="Water">Water</MenuItem>
                            <MenuItem value="Steam">Steam</MenuItem>
                            <MenuItem value="Elec">Elec</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', pr: 1 }}>
                    {heatingCoilType === 'None' || heatingCoilType === 'Steam' || heatingCoilType === 'Elec' ? (
                      <Typography variant="body2" color="textSecondary">-</Typography>
                    ) : (
                      <Controller
                        name="heatingInletTemp"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} size="small" sx={{ width: 60 }} inputProps={{ style: { textAlign: 'right' } }} />
                        )}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ position: 'relative', pl: 0.5 }}>
                    {heatingCoilType === 'None' || heatingCoilType === 'Steam' || heatingCoilType === 'Elec' ? (
                      <Typography variant="body2" color="textSecondary">-</Typography>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" color="textSecondary" sx={{ minWidth: '10px' }}>/</Typography>
                        <Controller
                          name="heatingOutletTemp"
                          control={control}
                          render={({ field }) => (
                            <TextField {...field} size="small" sx={{ width: 60 }} />
                          )}
                        />
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {heatingCoilType === 'Steam' ? (
                      <Controller
                        name="heatingHeatCapacity"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} size="small" sx={{ width: 80 }} placeholder="2454" />
                        )}
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {heatingCoilType === 'None' ? (
                      <Typography variant="body2" color="textSecondary">-</Typography>
                    ) : (
                      <Controller
                        name="heatingSafetyFactor"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} size="small" sx={{ width: 60 }} />
                        )}
                      />
                    )}
                  </TableCell>
                </TableRow>

               

              </TableBody>
            </Table>
          </TableContainer>

          {/* 코일 선택 유효성 검증 에러 메시지 */}
          {coilValidationError && (
            <Box sx={{ mt: 1, p: 1, bgcolor: '#ffebee', borderRadius: 1 }}>
              <Typography variant="body2" color="error">
                ⚠️ {coilValidationError}
              </Typography>
            </Box>
          )}

        </Paper>
      </Grid>

      {/* 3. 가습기 선정 기준 (Humidifier Selection Criteria) */}
      <Grid item xs={12}>
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom sx={{
            bgcolor: 'grey.200', fontWeight: 600,
            p: 1,
            mb: 2,
            borderRadius: 1
          }}>
            3. 가습기 선정 기준
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>열원 종류</Typography>
            <Controller
              name="humidifierType"
              control={control}
              render={({ field }) => (
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <Select {...field}>
                    <MenuItem value="None">None</MenuItem>
                    <MenuItem value="Steam(I)">Steam(I)</MenuItem>
                    <MenuItem value="Steam(O)">Steam(O)</MenuItem>
                    <MenuItem value="Water Supply">Water Supply</MenuItem>
                    <MenuItem value="Evap.(B)">Evap.(B)</MenuItem>
                  </Select>
                </FormControl>
              )}
            />

            {humidifierType === 'Water Supply' && (
              <>
                <Controller
                  name="humidifierInstallLocation"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          size="small"
                        />
                      }
                      label="설치위치(가열코일 전)"
                    />
                  )}
                />
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>공급수온(℃)</Typography>
                <Controller
                  name="waterSupplyTemp"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} size="small" sx={{ width: 80 }} />
                  )}
                />
              </>
            )}

            {humidifierType !== 'None' && (
              <>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>안전율(%)</Typography>
                <Controller
                  name="humidifierSafetyFactor"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} size="small" sx={{ width: 80 }} />
                  )}
                />
              </>
            )}
          </Box>

          {/* 가습기 종류 정보 */}
          <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1, borderLeft: '4px solid #2196f3' }}>
            <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.secondary', mb: 1 }}>
              💡 가습기 종류
            </Typography>
            <Box sx={{ ml: 1 }}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', lineHeight: 1.4 }}>
                • Steam(I), (O) : 증기 가습기, 공조기 내부, 외부 설치
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', lineHeight: 1.4 }}>
                • Water Supply : 수 가습기, 수온으로 공기가 냉각, 가열 발생
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', lineHeight: 1.4 }}>
                • Evap.(B) : 물을 가열하여 증발하는 가습기 (수온=공기온도)
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  )
}

export default EquipmentStandardsTab